import type React from "react"
import { Link } from "react-router-dom"
import { cn } from "../../lib/utils"

interface PageHeaderProps {
	/**
	 * Mono-caps context line above the title (design's editorial header,
	 * e.g. the stages page's "HARMONOGRAM PRAC"). Pages pass existing data
	 * (property name, role label) — never invented copy.
	 */
	eyebrow?: string
	/**
	 * Makes a contextual eyebrow (property name) an upward-path link
	 * (ZPL-353): with the fixed desktop bar retired, this anchor — styled
	 * like the plain eyebrow, underlined on hover — is how a page points
	 * back up without browser-back.
	 */
	eyebrowHref?: string
	title: React.ReactNode
	/** Optional intro paragraph under the title (wizard-style headers). */
	intro?: React.ReactNode
	/** Right-aligned zone: page-level summary figures and/or actions. */
	meta?: React.ReactNode
	className?: string
}

/**
 * The design's editorial page header (ZPL-350): eyebrow + display h1 (+
 * optional intro) on the left, stats/actions right-aligned on desktop and
 * wrapping below the title on mobile. Owns structure and alignment only —
 * type scale stays with the typography workstream.
 */
const EYEBROW_CLASS =
	"mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground"

export function PageHeader({
	eyebrow,
	eyebrowHref,
	title,
	intro,
	meta,
	className,
}: PageHeaderProps) {
	return (
		<header
			className={cn(
				"mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between",
				className,
			)}
		>
			<div className="min-w-0">
				{eyebrow && eyebrowHref ? (
					<Link
						to={eyebrowHref}
						className={cn(
							EYEBROW_CLASS,
							"inline-block hover:underline hover:text-foreground transition-colors",
						)}
					>
						{eyebrow}
					</Link>
				) : eyebrow ? (
					<p className={EYEBROW_CLASS}>{eyebrow}</p>
				) : null}
				<h1 className="text-3xl font-black tracking-tight text-foreground">
					{title}
				</h1>
				{intro ? (
					<p className="mt-2 max-w-prose text-sm text-foreground-secondary">
						{intro}
					</p>
				) : null}
			</div>
			{meta ? (
				<div className="flex min-w-0 flex-wrap items-center gap-3 md:shrink-0 md:justify-end">
					{meta}
				</div>
			) : null}
		</header>
	)
}
